/**
 * Barre de mise en forme : la garder invisible tant qu'elle n'est **réellement**
 * pas placée, et réclamer son placement jusqu'à l'obtenir.
 *
 * Tiptap affiche la barre puis calcule sa position, et ce calcul est asynchrone
 * (`computePosition` de floating-ui rend une promesse). Deux choses tournent mal
 * dans cet intervalle :
 *
 * 1. la barre est déjà dans le DOM sans coordonnées — elle se peint en haut de
 *    son conteneur ;
 * 2. si l'élément n'est pas encore rattaché quand la promesse retombe, tiptap
 *    **abandonne** le placement (`!this.element.isConnected`) et personne ne le
 *    redemande : la barre reste en haut jusqu'à la sélection suivante, qui
 *    réutilise les coordonnées d'alors. D'où un défaut qui ne se voit qu'à la
 *    première sélection de chaque texte.
 *
 * On efface donc la barre à l'affichage, on redemande le placement tant qu'il
 * n'a pas eu lieu, et on la rend au premier placement obtenu.
 *
 * @param {() => HTMLElement | null} getElement    la coquille de la barre
 * @param {object} [options]
 * @param {() => void} [options.requestPlacement]  redemande un placement à tiptap
 * @param {number[]} [options.retryDelays]         quand redemander, en ms
 * @param {number} [options.revealDelay]           filet : mieux vaut une barre mal
 *   placée qu'une barre invisible
 */
export function createBubblePositioningHooks(getElement, {
  requestPlacement = null,
  retryDelays = [0, 50, 150, 300],
  revealDelay = 400,
} = {}) {
  let timers = [];
  let placed = false;

  const set = (positioning) => {
    const el = getElement();
    if (el) el.dataset.positioning = positioning ? 'true' : 'false';
  };

  const clear = () => {
    timers.forEach(clearTimeout);
    timers = [];
  };

  return {
    onShow: () => {
      clear();
      placed = false;
      set(true);
      if (requestPlacement) {
        retryDelays.forEach((delay) => {
          timers.push(setTimeout(() => {
            if (placed) return;
            requestPlacement();
          }, delay));
        });
      }
      timers.push(setTimeout(() => set(false), revealDelay));
    },
    onUpdate: () => {
      placed = true;
      clear();
      set(false);
    },
    onHide: () => {
      clear();
      placed = false;
    },
    dispose: clear,
  };
}
